import { ConditionNode, LogicalNode, QueryNode } from "modules/record/record-producer/types/query.types";

export type ConditionEvaluationContext = Record<string, unknown> & {
    tableInheritance?: Record<string, string[]>;
    form?: Record<string, unknown>;
    user?: {
        id?: string;
        roles?: string[];
        [key: string]: unknown;
    } | null;
};

export function normalizeCondition(rawCondition: unknown): QueryNode | null {
    if (typeof rawCondition === "string") {
        if (rawCondition.trim() === "" || rawCondition.trim() === "null") {
            return null;
        }

        try {
            return normalizeCondition(JSON.parse(rawCondition));
        } catch (error) {
            console.error("Failed to parse condition", error);
            return null;
        }
    }

    if (!rawCondition || typeof rawCondition !== "object") {
        return null;
    }

    const condition = rawCondition as QueryNode;
    if (condition.type !== "condition" && condition.type !== "logical" && condition.type !== "exists") {
        return null;
    }

    return condition;
}

export function evaluateConditionAst(
    rawCondition: unknown,
    context: ConditionEvaluationContext
): boolean {
    if (
        !rawCondition ||
        (typeof rawCondition === "string" && ["", "null"].includes(rawCondition.trim()))
    ) {
        return true;
    }

    const condition = normalizeCondition(rawCondition);

    if (!condition) {
        return false;
    }

    return evaluateQueryNode(condition, context);
}

function evaluateQueryNode(node: QueryNode, context: ConditionEvaluationContext): boolean {
    if (node.type === "logical") {
        return evaluateLogicalNode(node, context);
    }

    if (node.type === "exists") {
        return false;
    }

    return evaluateConditionNode(node, context);
}

function evaluateLogicalNode(node: LogicalNode, context: ConditionEvaluationContext): boolean {
    const results = node.conditions.map((condition) => evaluateQueryNode(condition, context));

    if (node.operator === "or") {
        return results.some(Boolean);
    }

    return results.every(Boolean);
}

function evaluateConditionNode(node: ConditionNode, context: ConditionEvaluationContext): boolean {
    const leftValue = resolveFieldValue(node.field, context);
    const operator = normalizeOperator(node.operator);
    const rightValue = resolveConditionValue(node.value, context);

    switch (operator) {
        case "eq":
            return areValuesEqual(leftValue, rightValue);
        case "neq":
            return !areValuesEqual(leftValue, rightValue);
        case "gt":
            return compareValues(leftValue, rightValue) > 0;
        case "gte":
            return compareValues(leftValue, rightValue) >= 0;
        case "lt":
            return compareValues(leftValue, rightValue) < 0;
        case "lte":
            return compareValues(leftValue, rightValue) <= 0;
        case "contains":
            return evaluateContainsOperator(leftValue, rightValue);
        case "not_contains":
            return !evaluateContainsOperator(leftValue, rightValue);
        case "stw":
            return stringifyValue(leftValue).toLocaleLowerCase().startsWith(stringifyValue(rightValue).toLocaleLowerCase());
        case "edw":
            return stringifyValue(leftValue).toLocaleLowerCase().endsWith(stringifyValue(rightValue).toLocaleLowerCase());
        case "in":
            return evaluateInOperator(leftValue, rightValue);
        case "in_table_chain":
        case "in_ancestor_chain": {
            const chain = context.tableInheritance?.[String(rightValue)] ?? [];
            const members = operator === "in_ancestor_chain" ? chain.slice(0, -1) : chain;
            return members.includes(String(leftValue));
        }
        case "between":
            return evaluateBetweenOperator(leftValue, rightValue);
        case "empty":
            return isNullish(leftValue);
        case "not_empty":
            return !isNullish(leftValue);
        default:
            return false;
    }
}

function normalizeOperator(operator: string): string {
    return operator?.replace(/_dynamic$/, "") ?? operator;
}

function resolveConditionValue(rawValue: unknown, context: ConditionEvaluationContext): unknown {
    if (Array.isArray(rawValue)) {
        return rawValue.map((item) => resolveConditionValue(item, context));
    }

    if (rawValue && typeof rawValue === "object") {
        const maybeRange = rawValue as Record<string, unknown>;

        if ("start" in maybeRange || "end" in maybeRange || "from" in maybeRange || "to" in maybeRange) {
            return {
                start: resolveConditionValue(maybeRange.start ?? maybeRange.from, context),
                end: resolveConditionValue(maybeRange.end ?? maybeRange.to, context),
            };
        }

        return rawValue;
    }

    if (typeof rawValue !== "string") {
        return rawValue;
    }

    const fieldReference = rawValue.match(/^\$\{(.+)\}$/);
    if (fieldReference) {
        return resolveFieldValue(fieldReference[1], context);
    }

    const userReference = rawValue.match(/^\{\{user\.(.+)\}\}$/);
    if (userReference) {
        return getPathValue((context.user ?? {}) as Record<string, unknown>, userReference[1]);
    }

    return rawValue;
}

function resolveFieldValue(path: string, context: ConditionEvaluationContext): unknown {
    if (!path) {
        return undefined;
    }

    if (path === "roles") {
        return context.user?.roles ?? context.roles ?? [];
    }

    if (path === "isNewRecord" || path === "currentTableName") {
        return context[path];
    }

    if (path.startsWith("user.")) {
        return getPathValue((context.user ?? {}) as Record<string, unknown>, path.slice(5));
    }

    if (path.startsWith("form.")) {
        return getPathValue((context.form ?? {}) as Record<string, unknown>, path.slice(5));
    }

    const directValue = getPathValue(context, path);
    if (directValue !== undefined) {
        return directValue;
    }

    if (context.form) {
        const formValue = getPathValue(context.form, path);
        if (formValue !== undefined) {
            return formValue;
        }
    }

    const lastSegment = path.split(".").pop();
    if (lastSegment && lastSegment !== path) {
        return resolveFieldValue(lastSegment, context);
    }

    return undefined;
}

function getPathValue(source: Record<string, unknown>, path: string): unknown {
    return path.split(".").reduce<unknown>((currentValue, segment) => {
        if (!currentValue || typeof currentValue !== "object") {
            return undefined;
        }

        return (currentValue as Record<string, unknown>)[segment];
    }, source);
}

function areValuesEqual(leftValue: unknown, rightValue: unknown): boolean {
    if (isNullish(leftValue) && isNullish(rightValue)) {
        return true;
    }

    return leftValue === rightValue;
}

function evaluateContainsOperator(leftValue: unknown, rightValue: unknown): boolean {
    if (Array.isArray(leftValue)) {
        return leftValue.map(String).includes(String(rightValue));
    }

    return stringifyValue(leftValue).toLocaleLowerCase().includes(stringifyValue(rightValue).toLocaleLowerCase());
}

function evaluateInOperator(leftValue: unknown, rightValue: unknown): boolean {
    if (Array.isArray(rightValue)) {
        if (Array.isArray(leftValue)) {
            const normalizedLeft = leftValue.map(String);
            return rightValue.map(String).some((item) => normalizedLeft.includes(item));
        }

        return rightValue.includes(leftValue);
    }

    if (Array.isArray(leftValue)) {
        return leftValue.map(String).includes(String(rightValue));
    }

    const normalizedRight = stringifyValue(rightValue);
    const normalizedLeft = stringifyValue(leftValue);

    if (!normalizedRight) {
        return false;
    }

    return normalizedRight
        .split(",")
        .map((item) => item.trim())
        .includes(normalizedLeft);
}

function evaluateBetweenOperator(leftValue: unknown, rightValue: unknown): boolean {
    if (isNullish(leftValue) || rightValue === null || rightValue === undefined) {
        return false;
    }

    const boundaries = normalizeBetweenValue(rightValue);
    if (!boundaries) {
        return false;
    }

    return compareValues(leftValue, boundaries.start) >= 0 && compareValues(leftValue, boundaries.end) <= 0;
}

function normalizeBetweenValue(value: unknown): { start: unknown; end: unknown } | null {
    if (Array.isArray(value) && value.length >= 2) {
        return {
            start: value[0],
            end: value[1],
        };
    }

    if (value && typeof value === "object") {
        const rangeValue = value as Record<string, unknown>;
        const start = rangeValue.start ?? rangeValue.from ?? rangeValue.min ?? rangeValue[0];
        const end = rangeValue.end ?? rangeValue.to ?? rangeValue.max ?? rangeValue[1];

        if (start !== undefined && end !== undefined) {
            return { start, end };
        }
    }

    if (typeof value === "string") {
        const parts = value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

        if (parts.length >= 2) {
            return {
                start: parts[0],
                end: parts[1],
            };
        }
    }

    return null;
}

function compareValues(leftValue: unknown, rightValue: unknown): number {
    const leftNumber = toComparableNumber(leftValue);
    const rightNumber = toComparableNumber(rightValue);

    if (leftNumber !== null && rightNumber !== null) {
        return leftNumber - rightNumber;
    }

    const leftText = stringifyValue(leftValue);
    const rightText = stringifyValue(rightValue);

    if (leftText === rightText) {
        return 0;
    }

    return leftText > rightText ? 1 : -1;
}

function toComparableNumber(value: unknown): number | null {
    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === "number") {
        return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
        const dateValue = Date.parse(value);
        if (!Number.isNaN(dateValue)) {
            return dateValue;
        }

        const numberValue = Number(value);
        return Number.isNaN(numberValue) ? null : numberValue;
    }

    return null;
}

function stringifyValue(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
}

function isNullish(value: unknown) {
    return value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);
}
